import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  Eye,
  Pencil,
  Plus,
  Power,
  Tag,
  Trash2,
} from 'lucide-react';
import apiService from '../../services/api';
import Modal from '../Modal';
import ConfirmDialog from '../ConfirmDialog';
import PromotionForm from './PromotionForm';
import {
  buildPromotionPayload,
  createEmptyPromotionForm,
  filterPromotionsByStatus,
  getPromotionStatus,
  getPromotionReductionLabel,
  getTargetingLabel,
  getChannelsLabel,
  promotionToForm,
  validatePromotionForm,
} from '../../utils/promotionAdminUtils';
import { resolveMerchantOperationalScope } from '../../utils/assistanceSession';

const STATUS_FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'active', label: 'En cours' },
  { id: 'scheduled', label: 'À venir' },
  { id: 'expired', label: 'Terminées' },
  { id: 'disabled', label: 'Désactivées' },
];

function getCurrentUser() {
  try {
    const raw = localStorage.getItem('admin_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function PromotionsManager({
  commercantId: fixedCommercantId = null,
  commercantName: fixedCommercantName = '',
  embedded = false,
}) {
  const [currentUser] = useState(() => getCurrentUser());
  const scope = resolveMerchantOperationalScope(currentUser);
  const isCommercant = Boolean(fixedCommercantId) || scope.scopedToMerchant;
  const userCommercantId =
    fixedCommercantId ||
    scope.commercantId ||
    null;

  const [promotions, setPromotions] = useState([]);
  const [commercants, setCommercants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [fideliteConfig, setFideliteConfig] = useState(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggleError, setToggleError] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [viewPromotion, setViewPromotion] = useState(null);
  const [form, setForm] = useState(() =>
    createEmptyPromotionForm(isCommercant ? userCommercantId : fixedCommercantId || '')
  );
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const resolvedCommercantName = useMemo(() => {
    if (fixedCommercantName) return fixedCommercantName;
    if (isCommercant && userCommercantId) {
      return commercants.find((c) => c.id === userCommercantId)?.nom || 'Mon commerce';
    }
    return commercants.find((c) => c.id === form.commercantId)?.nom || '';
  }, [
    fixedCommercantName,
    isCommercant,
    userCommercantId,
    commercants,
    form.commercantId,
  ]);

  const loadPromotions = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      let data;
      if (userCommercantId && (isCommercant || fixedCommercantId)) {
        data = await apiService.request(`/promotions?commercantId=${userCommercantId}`);
      } else if (isCommercant && !userCommercantId) {
        setError('Votre compte n’est pas rattaché à une boutique.');
        data = [];
      } else {
        data = await apiService.getPromotions();
      }
      setPromotions(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || 'Impossible de charger les promotions');
      setPromotions([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [userCommercantId, isCommercant, fixedCommercantId]);

  const loadFideliteConfig = useCallback(async (commercantId) => {
    if (!commercantId) {
      setFideliteConfig(null);
      return;
    }
    try {
      const config = await apiService.request(`/fidelite/config/${commercantId}`);
      setFideliteConfig(config?.programme || config || null);
    } catch {
      setFideliteConfig(null);
    }
  }, []);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  useEffect(() => {
    if (!isCommercant && !fixedCommercantId) {
      apiService
        .getCommercants()
        .then((data) => setCommercants(Array.isArray(data) ? data : data?.commercants || []))
        .catch(() => setCommercants([]));
    } else if (userCommercantId && fixedCommercantName) {
      setCommercants([{ id: userCommercantId, nom: fixedCommercantName }]);
    }
  }, [isCommercant, fixedCommercantId, userCommercantId, fixedCommercantName]);

  useEffect(() => {
    const cid = form.commercantId || userCommercantId || fixedCommercantId;
    loadFideliteConfig(cid);
  }, [form.commercantId, userCommercantId, fixedCommercantId, loadFideliteConfig]);

  const filteredPromotions = useMemo(
    () => filterPromotionsByStatus(promotions, filter),
    [promotions, filter]
  );

  const openCreate = () => {
    setEditingId(null);
    setFormError('');
    setForm(
      createEmptyPromotionForm(
        fixedCommercantId || (isCommercant ? userCommercantId : '') || ''
      )
    );
    setShowFormModal(true);
  };

  const openEdit = async (promotion) => {
    setFormError('');
    setEditingId(promotion.id);
    try {
      const full = await apiService.getPromotion(promotion.id);
      setForm({
        ...promotionToForm(full, userCommercantId || fixedCommercantId || ''),
        actif: full.actif !== false,
      });
      setShowFormModal(true);
    } catch (e) {
      setError(e?.message || 'Impossible de charger la promotion');
    }
  };

  const openView = async (promotion) => {
    try {
      const full = await apiService.getPromotion(promotion.id);
      setViewPromotion(full);
      setShowViewModal(true);
    } catch {
      setViewPromotion(promotion);
      setShowViewModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validatePromotionForm(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSubmitting(true);
    setFormError('');
    try {
      const payload = buildPromotionPayload(form, { isEdit: Boolean(editingId) });
      const saved = editingId
        ? await apiService.updatePromotion(editingId, payload)
        : await apiService.createPromotion(payload);
      setShowFormModal(false);
      setEditingId(null);
      if (saved?.id) {
        setPromotions((prev) =>
          editingId
            ? prev.map((p) => (p.id === saved.id ? { ...p, ...saved } : p))
            : [saved, ...prev]
        );
        loadPromotions({ silent: true });
      } else {
        await loadPromotions({ silent: true });
      }
    } catch (err) {
      setFormError(err?.message || 'Enregistrement impossible');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!toggleTarget) return false;
    setActionLoading(true);
    setToggleError('');
    try {
      await apiService.updatePromotion(toggleTarget.id, {
        actif: !toggleTarget.actif,
      });
      setToggleTarget(null);
      setPromotions((prev) =>
        prev.map((p) =>
          p.id === toggleTarget.id ? { ...p, actif: !toggleTarget.actif } : p
        )
      );
      loadPromotions({ silent: true });
      return true;
    } catch (e) {
      const message = e?.message || 'Mise à jour impossible';
      setToggleError(message);
      setError(message);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return false;
    setActionLoading(true);
    try {
      const deletedId = deleteTarget.id;
      await apiService.deletePromotion(deletedId);
      setDeleteTarget(null);
      setPromotions((prev) => prev.filter((p) => p.id !== deletedId));
      loadPromotions({ silent: true });
      return true;
    } catch (e) {
      setError(e?.message || 'Suppression impossible');
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const getCommercantName = (id) =>
    commercants.find((c) => c.id === id)?.nom || '—';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6'}>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className={`font-bold text-gray-900 ${embedded ? 'text-lg' : 'text-2xl'}`}>
            Codes promo
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Créez un code, choisissez qui en profite et définissez la période de validité.
          </p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary inline-flex items-center">
          <Plus className="h-4 w-4 mr-2" />
          Créer une promotion
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              filter === f.id
                ? 'bg-primary-100 text-primary-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
            {f.id === 'all'
              ? ` (${promotions.length})`
              : ` (${filterPromotionsByStatus(promotions, f.id).length})`}
          </button>
        ))}
      </div>

      <div className="card overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Promotion</th>
              {!isCommercant && !fixedCommercantId && (
                <th className="px-4 py-3 text-left font-medium text-gray-500">Commerçant</th>
              )}
              <th className="px-4 py-3 text-left font-medium text-gray-500">Réduction</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Code</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Ciblage</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Période</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Statut</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {filteredPromotions.length === 0 ? (
              <tr>
                <td
                  colSpan={isCommercant || fixedCommercantId ? 7 : 8}
                  className="px-4 py-12 text-center text-gray-500"
                >
                  Aucune promotion pour ce filtre.
                </td>
              </tr>
            ) : (
              filteredPromotions.map((promotion) => {
                const status = getPromotionStatus(promotion);
                return (
                  <tr key={promotion.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{promotion.titre}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">
                        {promotion.description}
                      </div>
                    </td>
                    {!isCommercant && !fixedCommercantId && (
                      <td className="px-4 py-3">{getCommercantName(promotion.commercantId)}</td>
                    )}
                    <td className="px-4 py-3 font-semibold text-green-700">
                      {getPromotionReductionLabel(promotion)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold">
                        {promotion.codePromo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{getTargetingLabel(promotion)}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>
                          {new Date(promotion.dateDebut).toLocaleDateString('fr-FR')} →{' '}
                          {new Date(promotion.dateFin).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${status.className}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openView(promotion)}
                          className="p-2 text-gray-500 hover:text-primary-600"
                          title="Voir"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(promotion)}
                          className="p-2 text-gray-500 hover:text-primary-600"
                          title="Modifier"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setToggleTarget(promotion)}
                          className="p-2 text-gray-500 hover:text-amber-600"
                          title={promotion.actif ? 'Désactiver' : 'Réactiver'}
                        >
                          <Power className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(promotion)}
                          className="p-2 text-gray-500 hover:text-red-600"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={showFormModal}
        onClose={() => {
          if (!submitting) {
            setShowFormModal(false);
            setEditingId(null);
          }
        }}
        title={editingId ? 'Modifier la promotion' : 'Créer une promotion'}
        maxWidth="max-w-3xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {formError}
            </div>
          )}
          <PromotionForm
            form={form}
            setForm={setForm}
            commercants={commercants}
            commercantName={resolvedCommercantName}
            isCommercant={isCommercant || Boolean(fixedCommercantId)}
            fixedCommercantId={userCommercantId || fixedCommercantId || ''}
            fideliteConfig={fideliteConfig}
            isEdit={Boolean(editingId)}
          />
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              disabled={submitting}
              onClick={() => setShowFormModal(false)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              {submitting ? 'Enregistrement…' : editingId ? 'Enregistrer' : 'Créer la promotion'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Détail de la promotion"
        maxWidth="max-w-lg"
      >
        {viewPromotion && (
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <Tag className="h-5 w-5 text-primary-600 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-900">{viewPromotion.titre}</p>
                <p className="text-gray-600 mt-1">{viewPromotion.description}</p>
              </div>
            </div>
            <p>
              <strong>Code :</strong> {viewPromotion.codePromo}
            </p>
            <p>
              <strong>Réduction :</strong> {getPromotionReductionLabel(viewPromotion)}
            </p>
            {viewPromotion.montantMinimum > 0 && (
              <p>
                <strong>Panier minimum :</strong> {viewPromotion.montantMinimum} €
              </p>
            )}
            <p>
              <strong>Ciblage :</strong> {getTargetingLabel(viewPromotion)}
            </p>
            <p>
              <strong>Canaux :</strong> {getChannelsLabel(viewPromotion)}
            </p>
            {viewPromotion.limiteUtilisationParUser && (
              <p>
                <strong>Limite / client :</strong> {viewPromotion.limiteUtilisationParUser}
              </p>
            )}
            <p>
              <strong>Statut :</strong>{' '}
              <span className={getPromotionStatus(viewPromotion).className + ' px-2 py-0.5 rounded-full text-xs'}>
                {getPromotionStatus(viewPromotion).label}
              </span>
            </p>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(toggleTarget)}
        title={toggleTarget?.actif ? 'Désactiver la promotion' : 'Réactiver la promotion'}
        description={
          toggleTarget?.actif
            ? `Le code ${toggleTarget?.codePromo} ne pourra plus être utilisé tant qu'il est désactivé.`
            : `Le code ${toggleTarget?.codePromo} sera à nouveau utilisable (sous réserve des dates).`
        }
        confirmLabel={toggleTarget?.actif ? 'Désactiver' : 'Réactiver'}
        tone={toggleTarget?.actif ? 'warning' : 'success'}
        confirmDisabled={actionLoading}
        onCancel={() => {
          setToggleTarget(null);
          setToggleError('');
        }}
        onConfirm={handleToggleActive}
      >
        {toggleError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {toggleError}
          </p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Supprimer la promotion"
        description={`Supprimer définitivement « ${deleteTarget?.titre} » ? Préférez « Désactiver » pour couper le code sans le perdre.`}
        confirmLabel="Supprimer"
        tone="danger"
        confirmDisabled={actionLoading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
